import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ShoppingCart, AlertCircle, CheckCircle, Search, Share2 } from 'lucide-react';
import orderLinkAPI, {
  Product,
  Menu,
  Store,
  OrderItem,
  OrderHistory,
  createPixPayment,
  getPixPaymentStatus,
} from '../services/orderLinkAPI';
import ProductGrid from './ProductGrid';
import { orderMenu } from '../utils/menuOrder';
import { applyMenuColors } from '../utils/menuColors';
import CartDrawer from './CartDrawer';
import CheckoutForm, { CheckoutFormData } from './CheckoutForm';
import { mergeFractionalPizzas, CartItem } from '../utils/pizzaMergeUtils';
import { getDirectImageUrl } from '../utils/imageUrl';
import BottomNavigation from './BottomNavigation';
import SearchModal from './SearchModal';
import OrderHistoryPage from './OrderHistoryPage';
import PixPaymentScreen from './PixPaymentScreen';

type PageState = 'loading' | 'menu' | 'checkout' | 'success' | 'error' | 'pix';
type TabType = 'home' | 'orders' | 'cart';

const OrderLinkPage: React.FC = () => {
  const { linkId } = useParams<{ linkId: string }>();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [store, setStore] = useState<Store | null>(null);
  const [menu, setMenu] = useState<Menu>({});
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderNumber, setOrderNumber] = useState<number | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [highlightedProductId, setHighlightedProductId] = useState<string | null>(null);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [currentTab, setCurrentTab] = useState<TabType>('home');
  const [orderHistory, setOrderHistory] = useState<OrderHistory[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [pixPaymentData, setPixPaymentData] = useState<any>(null);
  const [pixOrderId, setPixOrderId] = useState<string | null>(null);
  const [pixToken, setPixToken] = useState<string | null>(null);

  // Refs para scroll automático
  const categoryRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const tabsContainerRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const productRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Função para centralizar tab ativa horizontalmente
  const scrollTabIntoView = useCallback((category: string) => {
    const tabElement = tabRefs.current[category];
    const containerElement = tabsContainerRef.current;

    if (tabElement && containerElement) {
      const tabLeft = tabElement.offsetLeft;
      const tabWidth = tabElement.offsetWidth;
      const containerWidth = containerElement.offsetWidth;

      // Calcula a posição para centralizar a tab
      const targetScrollLeft = tabLeft - (containerWidth / 2) + (tabWidth / 2);

      // Scroll suave horizontal
      containerElement.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth'
      });
    }
  }, []);

  useEffect(() => {
    if (linkId) {
      loadOrderLink();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkId]);

  // Scroll spy - detecta qual categoria está visível
  useEffect(() => {
    if (pageState !== 'menu') return;

    const handleScroll = () => {
      const categories = Object.keys(menu);
      const scrollPosition = window.scrollY + 100; // Offset para tabs sticky

      for (let i = categories.length - 1; i >= 0; i--) {
        const category = categories[i];
        const element = categoryRefs.current[category];

        if (element && element.offsetTop <= scrollPosition) {
          setSelectedCategory(category);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [menu, pageState]);

  // Centraliza a tab ativa quando a categoria muda
  useEffect(() => {
    if (selectedCategory) {
      scrollTabIntoView(selectedCategory);
    }
  }, [selectedCategory, scrollTabIntoView]);

  const loadOrderLink = async () => {
    try {
      setPageState('loading');

      // Validate link
      const validation = await orderLinkAPI.validateOrderLink(linkId!);

      if (!validation.valid) {
        setErrorMessage(validation.error || 'Link inválido');
        setPageState('error');
        return;
      }

      setStore(validation.store!);
      applyMenuColors(validation.store!.cores_cardapio);

      // Load menu
      const menuData = await orderLinkAPI.getMenuByLink(linkId!);

      const processedMenu: Menu = orderMenu(menuData.menu, menuData.category_order);

      setMenu(processedMenu);

      // Select first category (will be Promoções if it exists)
      const categories = Object.keys(processedMenu);
      if (categories.length > 0) {
        setSelectedCategory(categories[0]);
      }

      setPageState('menu');
    } catch (error: any) {
      console.error('Error loading order link:', error);
      setErrorMessage(
        error.response?.data?.error || 'Erro ao carregar cardápio'
      );
      setPageState('error');
    }
  };

  const handleAddToCart = (
    product: Product,
    quantity: number,
    attributes?: Record<string, any>,
    finalPrice?: number
  ) => {
    setCartItems((prev) => {
      // Create item signature based on product_id + attributes
      const newItemSignature = JSON.stringify({
        productId: product.id,
        attributes: attributes || {}
      });

      const existingIndex = prev.findIndex((item: any) => {
        const itemSignature = JSON.stringify({
          productId: item.product.id,
          attributes: item.product_variables || {}
        });
        return itemSignature === newItemSignature;
      });

      if (existingIndex >= 0) {
        // Update existing item with same product and attributes
        const updated = [...prev];
        updated[existingIndex].quantity += quantity;

        // Remove item if quantity is 0 or less
        if (updated[existingIndex].quantity <= 0) {
          return updated.filter((_, index) => index !== existingIndex);
        }

        return updated;
      } else {
        // Add new item only if quantity is positive
        if (quantity > 0) {
          return [
            ...prev,
            {
              product,
              quantity,
              product_variables: attributes || {},
              finalPrice: finalPrice
            }
          ];
        }
        return prev;
      }
    });
  };

  // Generate unique identifier for cart item based on product + attributes
  const getItemSignature = (productId: string, attributes?: Record<string, any>): string => {
    return JSON.stringify({
      productId: productId,
      attributes: attributes || {}
    });
  };

  const handleUpdateQuantity = (productId: string, attributes: Record<string, any> | undefined, quantity: number) => {
    if (quantity <= 0) {
      handleRemoveItem(productId, attributes);
      return;
    }

    const targetSignature = getItemSignature(productId, attributes);
    setCartItems((prev) =>
      prev.map((item: any) => {
        const itemSignature = getItemSignature(item.product.id, item.product_variables);
        return itemSignature === targetSignature ? { ...item, quantity } : item;
      })
    );
  };

  const handleRemoveItem = (productId: string, attributes?: Record<string, any>) => {
    const targetSignature = getItemSignature(productId, attributes);
    setCartItems((prev) => prev.filter((item: any) => {
      const itemSignature = getItemSignature(item.product.id, item.product_variables);
      return itemSignature !== targetSignature;
    }));
  };

  const handleRemoveMergedItem = (mergedItem: CartItem) => {
    // Remove all original items that make up this merged combo
    const originalItems = (mergedItem as any).items || [];
    if (originalItems.length === 0) return;

    setCartItems((prev) => {
      return prev.filter((item: any) => {
        const itemSignature = getItemSignature(item.product.id, item.product_variables);

        // Check if this item is one of the original items in the merged combo
        return !originalItems.some((originalItem: CartItem) => {
          const originalSignature = getItemSignature(originalItem.product.id, originalItem.product_variables);
          return itemSignature === originalSignature;
        });
      });
    });
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) return;
    setPageState('checkout');
    setIsCartOpen(false);
    window.scrollTo(0, 0);
  };

  const handleSubmitOrder = async (formData: CheckoutFormData) => {
    try {
      setIsSubmitting(true);

      // Prepare order items
      const items: OrderItem[] = cartItems.map((item: any) => ({
        product_id: item.product.id,
        quantity: item.quantity,
        product_variables: item.product_variables || {}
      }));

      // Create order
      const response = await orderLinkAPI.createOrderFromLink(linkId!, {
        ...formData,
        items,
      });

      if (response.success) {
        // Check if Pix MP payment is pending
        if (response.pix_mp_pending && response.pix_token) {
          try {
            const pixResponse = await createPixPayment(response.pix_token);
            if (pixResponse.success) {
              setPixPaymentData(pixResponse.order_payment);
              setPixOrderId(response.order.id);
              setPixToken(response.pix_token);
              setOrderNumber(response.order.daily_order_id);
              setPageState('pix');
              return;
            }
          } catch (pixError: any) {
            console.error('Error creating PIX payment:', pixError);
            // Fall through to success if PIX creation fails
          }
        }
        setOrderNumber(response.order.daily_order_id);
        setPageState('success');
      }
    } catch (error: any) {
      console.error('Error creating order:', error);
      setErrorMessage(
        error.response?.data?.error || 'Erro ao criar pedido'
      );
      setPageState('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCartItemsMap = (): Map<string, number> => {
    const map = new Map<string, number>();
    cartItems.forEach((item: any) => {
      map.set(item.product.id, item.quantity);
    });
    return map;
  };

  // Apply pizza merge logic if cobrar_preco_maior is enabled
  const mergedCartItems = useMemo(() => {
    return mergeFractionalPizzas(cartItems, store?.cobrar_preco_maior);
  }, [cartItems, store?.cobrar_preco_maior]);

  const getTotalItems = (): number => {
    return mergedCartItems.reduce((sum, item) => sum + item.quantity, 0);
  };

  // Função para scroll suave até a categoria
  const scrollToCategory = (category: string) => {
    const element = categoryRefs.current[category];
    if (element) {
      const headerOffset = 70; // Altura das tabs sticky
      const elementPosition = element.offsetTop;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setSelectedCategory(category);
    }
  };

  // Função para scroll até produto específico e destacá-lo
  const handleProductSelect = (productId: string, category: string) => {
    const productElement = productRefs.current[productId];

    if (productElement) {
      const headerOffset = 70; // Altura das tabs sticky
      const elementPosition = productElement.offsetTop;
      const offsetPosition = elementPosition - headerOffset;

      // Scroll até o produto
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      // Destacar o produto
      setHighlightedProductId(productId);

      // Remover destaque após 2 segundos
      setTimeout(() => {
        setHighlightedProductId(null);
      }, 2000);

      // Atualizar categoria selecionada
      setSelectedCategory(category);
    }
  };

  // Função para compartilhar o cardápio
  const handleShare = async () => {
    const shareData = {
      title: store?.name || 'Cardápio',
      text: `Confira o cardápio de ${store?.name || 'nossa loja'}!`,
      url: window.location.href
    };

    try {
      // Tentar usar Web Share API (nativo mobile)
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: copiar link para clipboard
        await navigator.clipboard.writeText(window.location.href);

        // Mostrar toast de confirmação
        setShowCopyToast(true);
        setTimeout(() => {
          setShowCopyToast(false);
        }, 3000);
      }
    } catch (error) {
      // Usuário cancelou ou erro ao compartilhar
      console.log('Compartilhamento cancelado ou erro:', error);
    }
  };

  // Função para carregar histórico de pedidos
  const loadOrderHistory = async () => {
    try {
      setIsLoadingHistory(true);
      const response = await orderLinkAPI.getClientOrders(linkId!);
      setOrderHistory(response.orders);
    } catch (error) {
      console.error('Error loading order history:', error);
      setOrderHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Função para fazer pedido novamente
  const handleReorder = (order: OrderHistory) => {
    // Para cada item do pedido
    order.items.forEach((item: any) => {
      // Buscar produto no menu pelo ID
      const product = findProductInMenu(item.product_id);

      if (product) {
        // Adicionar ao carrinho com mesmos atributos
        handleAddToCart(
          product,
          item.quantity,
          item.product_variables,
          item.unit_price
        );
      }
    });

    // Voltar para aba Início
    setCurrentTab('home');

    // Mostrar toast de confirmação
    setShowCopyToast(true);
    setTimeout(() => {
      setShowCopyToast(false);
    }, 3000);
  };

  // Função para buscar produto no menu pelo ID
  const findProductInMenu = (productId: string): Product | null => {
    for (const category in menu) {
      const product = menu[category].find((p: Product) => p.id === productId);
      if (product) {
        return product;
      }
    }
    return null;
  };

  // Loading State
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Carregando cardápio...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (pageState === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-card p-8 text-center">
          <AlertCircle className="w-16 h-16 text-error-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Ops! Algo deu errado
          </h1>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <button
            onClick={loadOrderLink}
            className="btn btn-primary"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  // Pix Payment State
  if (pageState === 'pix' && pixPaymentData) {
    return (
      <PixPaymentScreen
        orderId={pixOrderId!}
        orderPaymentData={pixPaymentData}
        onSuccess={() => {
          setPageState('success');
        }}
        onGenerateNew={async () => {
          const newPix = await createPixPayment(pixToken!);
          if (newPix.success) {
            setPixPaymentData(newPix.order_payment);
          }
        }}
        getPixPaymentStatus={getPixPaymentStatus}
      />
    );
  }

  // Success State
  if (pageState === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-card p-8 text-center">
          <CheckCircle className="w-16 h-16 text-success-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Pedido Confirmado!
          </h1>
          {orderNumber && (
            <p className="text-lg font-semibold text-primary-600 mb-4">
              Número do pedido: #{orderNumber}
            </p>
          )}
          <p className="text-gray-600 mb-6">
            Seu pedido foi recebido com sucesso! Você receberá uma confirmação no WhatsApp em breve.
          </p>
          <div className="bg-success-50 border border-success-200 rounded-lg p-4">
            <p className="text-success-800 text-sm">
              Em breve seu pedido estará pronto!
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Checkout State
  if (pageState === 'checkout') {
    return (
      <CheckoutForm
        cartItems={mergedCartItems}
        meiosPagamento={store?.meios_pagamento}
        linkId={linkId!}
        onSubmit={handleSubmitOrder}
        onBack={() => setPageState('menu')}
        isSubmitting={isSubmitting}
      />
    );
  }

  // Menu State
  const categories = Object.keys(menu);
  const totalItems = getTotalItems();

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        {store?.banner_image_path ? (
          // Layout WITH BANNER
          <>
            {/* Top Bar with Search and Share */}
            <div className="max-w-4xl mx-auto px-4 pt-4">
              <div className="flex items-center justify-end gap-2 mb-2">
                <button
                  onClick={() => setIsSearchOpen(true)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <Search className="w-5 h-5" style={{ stroke: 'var(--text-color)' }} />
                </button>
                <button
                  onClick={handleShare}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <Share2 className="w-5 h-5" style={{ stroke: 'var(--text-color)' }} />
                </button>
              </div>
            </div>

            {/* Banner with Logo Overlay */}
            <div className="relative w-full h-[150px] bg-gray-200 overflow-hidden">
              <img
                src={getDirectImageUrl(store.banner_image_path)}
                alt={`Banner ${store.name}`}
                className="w-full h-full object-cover"
              />

              {/* Logo Overlay - Left Center */}
              <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-3">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full border-4 border-white shadow-lg overflow-hidden bg-white flex items-center justify-center">
                  {store?.image_path ? (
                    <img
                      src={getDirectImageUrl(store.image_path)}
                      alt={store.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-3xl font-bold text-primary-600">
                      {store?.name?.charAt(0).toUpperCase() || 'R'}
                    </span>
                  )}
                </div>

                {/* Store Name - Next to Logo */}
                <div className="hidden sm:block">
                  <h1 className="text-xl md:text-2xl font-bold text-white drop-shadow-lg">
                    {store?.name || 'Cardápio'}
                  </h1>
                </div>
              </div>
            </div>

            {/* Store Info Below Banner */}
            <div className="max-w-4xl mx-auto px-4 py-3">
              {/* Store Name for Mobile (visible when name doesn't fit next to logo) */}
              <h1 className="text-[26px] leading-tight font-extrabold tracking-tight text-gray-900 mb-1 sm:hidden">
                {store?.name || 'Cardápio'}
              </h1>

              {/* Store Info */}
              <div className="flex items-center gap-2 text-[13px] text-gray-600 mb-2 font-mono">
                <span>
                  {store?.hora_fechamento
                    ? `Aberto até ${store.hora_fechamento}`
                    : 'Aberto'}
                </span>
                {store?.pedido_minimo && (
                  <>
                    <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                    <span>Pedido mín. R$ {store.pedido_minimo.toFixed(2).replace('.', ',')}</span>
                  </>
                )}
              </div>

              {/* Social Media Links */}
              {(store?.instagram_link || store?.facebook_link || store?.twitter_link) && (
                <div className="flex gap-3">
                  {store?.instagram_link && (
                    <a
                      href={store.instagram_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 flex items-center justify-center rounded-full hover:opacity-70 transition-opacity"
                    >
                      <img
                        src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png"
                        alt="Instagram"
                        className="w-6 h-6"
                      />
                    </a>
                  )}
                  {store?.facebook_link && (
                    <a
                      href={store.facebook_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 flex items-center justify-center rounded-full hover:opacity-70 transition-opacity"
                    >
                      <img
                        src="https://upload.wikimedia.org/wikipedia/commons/5/51/Facebook_f_logo_%282019%29.svg"
                        alt="Facebook"
                        className="w-6 h-6"
                      />
                    </a>
                  )}
                  {store?.twitter_link && (
                    <a
                      href={store.twitter_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 flex items-center justify-center rounded-full hover:opacity-70 transition-opacity"
                    >
                      <img
                        src="https://upload.wikimedia.org/wikipedia/commons/6/6f/Logo_of_Twitter.svg"
                        alt="Twitter"
                        className="w-6 h-6"
                      />
                    </a>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          // Layout WITHOUT BANNER (fallback - original design)
          <div className="max-w-4xl mx-auto px-4 py-4">
            {/* Top Bar with Search and Share */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1"></div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsSearchOpen(true)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <Search className="w-5 h-5" style={{ stroke: 'var(--text-color)' }} />
                </button>
                <button
                  onClick={handleShare}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <Share2 className="w-5 h-5" style={{ stroke: 'var(--text-color)' }} />
                </button>
              </div>
            </div>

            {/* Store Logo and Name */}
            <div className="flex flex-col items-center text-center mb-3">
              <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mb-2 border-2 border-primary-200 overflow-hidden">
                {store?.image_path ? (
                  <img
                    src={getDirectImageUrl(store.image_path)}
                    alt={store.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-2xl font-bold text-primary-600">
                    {store?.name?.charAt(0).toUpperCase() || 'R'}
                  </span>
                )}
              </div>
              <h1 className="text-[26px] leading-tight font-extrabold tracking-tight text-gray-900">
                {store?.name || 'Cardápio'}
              </h1>
            </div>

            {/* Store Info */}
            <div className="flex items-center justify-center gap-2 text-[13px] text-gray-600 mb-2 font-mono">
              <span>
                {store?.hora_fechamento
                  ? `Aberto até ${store.hora_fechamento}`
                  : 'Aberto'}
              </span>
              {store?.pedido_minimo && (
                <>
                  <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                  <span>Pedido mín. R$ {store.pedido_minimo.toFixed(2).replace('.', ',')}</span>
                </>
              )}
            </div>

            {/* Social Media Links */}
            {(store?.instagram_link || store?.facebook_link || store?.twitter_link) && (
              <div className="flex justify-center gap-3">
                {store?.instagram_link && (
                  <a
                    href={store.instagram_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:opacity-70 transition-opacity"
                  >
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png"
                      alt="Instagram"
                      className="w-6 h-6"
                    />
                  </a>
                )}
                {store?.facebook_link && (
                  <a
                    href={store.facebook_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:opacity-70 transition-opacity"
                  >
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/5/51/Facebook_f_logo_%282019%29.svg"
                      alt="Facebook"
                      className="w-6 h-6"
                    />
                  </a>
                )}
                {store?.twitter_link && (
                  <a
                    href={store.twitter_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:opacity-70 transition-opacity"
                  >
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/6/6f/Logo_of_Twitter.svg"
                      alt="Twitter"
                      className="w-6 h-6"
                    />
                  </a>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Category Tabs */}
      {categories.length > 0 && (
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4">
            <div
              ref={tabsContainerRef}
              className="flex gap-2 overflow-x-auto py-3 scrollbar-hide"
            >
              {categories.map((category) => {
                const isPromotion = category.includes('🔥') || category.toLowerCase().includes('promoç');
                return (
                  <button
                    key={category}
                    ref={(el) => {
                      tabRefs.current[category] = el;
                    }}
                    onClick={() => scrollToCategory(category)}
                    className={`relative min-h-[42px] px-4 py-2 rounded-md font-bold whitespace-nowrap transition-colors text-[15px] flex items-center gap-2 border-[1.5px] ${
                      selectedCategory === category
                        ? 'text-white'
                        : 'bg-transparent text-gray-900 border-gray-900 hover:bg-gray-100'
                    }`}
                    style={selectedCategory === category ? {
                      backgroundColor: 'var(--bg-color)',
                      borderColor: 'var(--bg-color)',
                    } : {}}
                  >
                    {category}
                    {isPromotion && (
                      <span className="ml-1 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold"
                        style={{
                          backgroundColor: selectedCategory === category ? 'rgba(255,255,255,0.2)' : 'var(--badge-color)',
                          color: 'white'
                        }}
                      >
                        %
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Home Tab - All Categories - Continuous Scroll */}
      {currentTab === 'home' && (
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-8">
          {categories.map((category) => (
            <div
              key={category}
              ref={(el) => {
                categoryRefs.current[category] = el;
              }}
              className="category-section"
            >
              {/* Category Title */}
              <h2 className="text-[28px] leading-none font-extrabold tracking-tight text-gray-900 mb-4">
                {category}
              </h2>

              {/* Products in this category */}
              <ProductGrid
                products={menu[category] || []}
                onAddToCart={handleAddToCart}
                cartItems={getCartItemsMap()}
                store={store || undefined}
                menu={menu}
                productRefs={productRefs}
                highlightedProductId={highlightedProductId}
              />
            </div>
          ))}
        </div>
      )}

      {/* Orders Tab - Order History */}
      {currentTab === 'orders' && (
        <OrderHistoryPage
          linkId={linkId!}
          orders={orderHistory}
          onReorder={handleReorder}
          isLoading={isLoadingHistory}
        />
      )}

      {/* Floating Cart Button */}
      {totalItems > 0 && (
        <button
          onClick={() => setIsCartOpen(true)}
          className="fixed bottom-20 right-6 text-white rounded-full p-4 shadow-[0_4px_12px_rgba(0,0,0,0.2)] hover:opacity-90 transition-all flex items-center gap-2"
          style={{ backgroundColor: 'var(--bg-color)' }}
        >
          <ShoppingCart className="w-6 h-6" />
          <span className="font-semibold text-[15px]">{totalItems}</span>
        </button>
      )}

      {/* Cart Drawer */}
      <CartDrawer
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={mergedCartItems}
        onUpdateQuantity={handleUpdateQuantity}
        onRemoveItem={handleRemoveItem}
        onRemoveMergedItem={handleRemoveMergedItem}
        onCheckout={handleCheckout}
        store={store || undefined}
      />

      {/* Bottom Navigation */}
      <BottomNavigation
        activeTab={currentTab}
        cartItemCount={totalItems}
        onTabChange={(tab) => {
          if (tab === 'cart' && totalItems > 0) {
            setIsCartOpen(true);
          } else if (tab === 'orders') {
            setCurrentTab('orders');
            loadOrderHistory();
          } else if (tab === 'home') {
            setCurrentTab('home');
          }
        }}
      />

      {/* Search Modal */}
      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        menu={menu}
        onProductSelect={handleProductSelect}
      />

      {/* Copy Link Toast */}
      {showCopyToast && (
        <div className="fixed bottom-32 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-[14px] font-medium">Link copiado para área de transferência!</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderLinkPage;
